import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Drone, droneLabel } from '../../types/drone';
import { ApiError } from '../../types/api';
import { SimulatorImages, VirtualDroneSoftware } from '../../types/virtualDrone';
import { virtualDroneService } from '../../services/virtualDroneService';
import { formatTimestamp } from '../../utils/formatters';
import { imageTag } from '../../utils/simulatorImages';

/** How often the section asks again while an update or a switch-on runs. */
export const CHANGE_POLL_MS = 3000;

type SoftwareState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'found'; software: VirtualDroneSoftware };

/** A change of the simulator the pilot is asked to confirm. */
type Confirmed = 'update' | 'switch_off';

const describeImages = (images: SimulatorImages): string =>
  `carrier-pigeon ${imageTag(images.carrier_pigeon)}, simulator ${imageTag(images.mavlink_simulator)}`;

/** Why the API refused a change of the simulator, in the pilot's words. */
const refusal = (err: ApiError, done: string): string => {
  switch (err.serverCode) {
    case 'INVALID_DRONE_STATE':
      return `The drone is ${done} on the ground, disarmed. Land it first.`;
    case 'SIMULATOR_BUSY':
      return "The drone's simulator is already being changed.";
    default:
      return 'The simulator could not be changed. Try again.';
  }
};

/** Whether a change of the simulator runs on the server, so the section asks again. */
const changing = (software: VirtualDroneSoftware): boolean => software.updating || software.switching_on;

/**
 * A virtual drone's simulator: its build, the pilot's update of it (#791),
 * and switching it off and on (#802).
 *
 * A roll of the pins changes only drones created after it; an older build
 * stays valid until the pilot updates it, when it suits them (V1), on the
 * ground (W2). Switched off, also on the ground, the simulator stops and the
 * drone stays, grey, until it is switched on (W1). The update and the
 * switch-off ask first. An update and a switch-on run on the server, and the
 * section asks how they went until they are over: a failure there is shown
 * here, or the pilot would never know.
 *
 * A physical drone is updated with fleetctl install (V3), so the section is
 * absent for one.
 */
export const VirtualDroneSoftwareSection: React.FC<{ drone: Drone }> = ({ drone }) => {
  const [state, setState] = useState<SoftwareState>({ kind: 'loading' });
  // What the dialog asks about stays set while it closes, so its words do
  // not turn into the other action's as it fades.
  const [asking, setAsking] = useState<Confirmed>('update');
  const [confirming, setConfirmingOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const confirm = (action: Confirmed) => {
    setAsking(action);
    setConfirmingOpen(true);
  };

  const load = useCallback(async () => {
    try {
      setState({ kind: 'found', software: await virtualDroneService.software(drone.id) });
    } catch {
      // One failed read while a change runs keeps what was shown, and the
      // polling with it; the next read tells how the change went.
      setState((shown) => (shown.kind === 'found' && changing(shown.software) ? shown : { kind: 'failed' }));
    }
  }, [drone.id]);

  useEffect(() => {
    // Another drone: nothing of the last one's stays on show.
    setState({ kind: 'loading' });
    setRefused(null);
    setConfirmingOpen(false);
    if (drone.virtual) void load();
  }, [drone.virtual, load]);

  const busy = state.kind === 'found' && changing(state.software);
  useEffect(() => {
    if (!busy) return;
    const timer = setInterval(() => void load(), CHANGE_POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  if (!drone.virtual) {
    return null;
  }

  const change = async (send: (id: string) => Promise<void>, done: string) => {
    setConfirmingOpen(false);
    setRefused(null);
    setSending(true);
    try {
      await send(drone.id);
      await load();
    } catch (err: unknown) {
      setRefused(refusal(err as ApiError, done));
    } finally {
      setSending(false);
    }
  };

  const headingId = `software-${drone.id}`;
  const software = state.kind === 'found' ? state.software : null;
  const stopped = software !== null && !software.running && !software.update_interrupted && !busy;
  // Stopped in the air some other way than Switch off is said as it is (W3):
  // it stays red, and may still be switched on, on the ground.
  const stoppedInTheAir = drone.status === 'shut_down_in_flight';
  return (
    <Box component="section" aria-labelledby={headingId} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography id={headingId} variant="subtitle2" component="h3">
        Simulator
      </Typography>

      {state.kind === 'loading' && <CircularProgress size={20} aria-label="Loading the simulator's build" />}
      {state.kind === 'failed' && (
        <Alert severity="warning">The simulator's build could not be read. Try again later.</Alert>
      )}

      {software && (
        <>
          {software.running && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                {describeImages(software.running)}
              </Typography>
              {!busy && (
                <Button size="small" disabled={sending} onClick={() => confirm('switch_off')}>
                  Switch off
                </Button>
              )}
            </Box>
          )}
          {stopped && (
            <Alert
              severity={stoppedInTheAir ? 'warning' : 'info'}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={sending}
                  onClick={() => void change(virtualDroneService.switchOn, 'switched on')}
                >
                  Switch on
                </Button>
              }
            >
              {stoppedInTheAir
                ? 'Shut down in the air: no simulator is running. Switched on, it starts on the ground at its spawn point.'
                : 'Switched off: no simulator is running.'}
            </Alert>
          )}

          {software.last_failure && (
            <Alert severity="error">
              {software.last_failure.message} ({formatTimestamp(software.last_failure.at)})
            </Alert>
          )}

          {software.updating && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Updating. The drone signs off and comes back within about a minute.
            </Alert>
          )}
          {software.switching_on && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Switching on. The drone comes back within about a minute.
            </Alert>
          )}
          {!busy && software.update_available && software.pinned && (
            <Alert
              severity={software.update_interrupted ? 'warning' : 'info'}
              action={
                <Button color="inherit" size="small" disabled={sending} onClick={() => confirm('update')}>
                  Update
                </Button>
              }
            >
              {software.update_interrupted
                ? `An update did not finish, so the drone has no simulator. Update finishes it: ${describeImages(software.pinned)}.`
                : `An update is available: ${describeImages(software.pinned)}.`}
            </Alert>
          )}
          {refused && <Alert severity="warning">{refused}</Alert>}
        </>
      )}

      <Dialog open={confirming} onClose={() => setConfirmingOpen(false)} aria-labelledby={`${headingId}-confirm`}>
        <DialogTitle id={`${headingId}-confirm`}>
          {asking === 'switch_off' ? 'Switch off' : 'Update'} {droneLabel(drone)}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {asking === 'switch_off'
              ? 'Its simulator stops. The drone stays in your fleet, offline, until you switch it on; it then starts on the ground at its spawn point.'
              : 'Its simulator restarts on the new build, on the ground at its spawn point. The drone keeps its id; it is offline for about a minute and cannot be commanded until it is back.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              void (asking === 'switch_off'
                ? change(virtualDroneService.switchOff, 'switched off')
                : change(virtualDroneService.update, 'updated'))
            }
          >
            {asking === 'switch_off' ? 'Switch off' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
